import { useState } from 'react';
import { api, getSession } from '../api/client.ts';
import { useAbonnement, dateDuJour } from '../hooks/useAbonnement.tsx';
import type { AbonnementInfo } from '../hooks/useAbonnement.tsx';
import {
  DUREES_MOIS, MODULES_PAR_PACK, MODULE_LIBELLES, PACKS, PACK_LIBELLES, PRIX_MENSUEL_PACK, prixPack,
} from '../../../shared/business-logic/abonnement.ts';
import type { DureeMois, Pack } from '../../../shared/business-logic/abonnement.ts';

const NUMERO_MVOLA = '034 21 132 40';
const NOM_BENEFICIAIRE = 'Serge Erica';

const fmtAr = (n: number) => `${n.toLocaleString('fr-FR')} Ar`;
const fmtDate = (d: string | null | undefined) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');

// Valeurs d'affichage tant que le serveur n'a jamais répondu et que rien n'est en cache local :
// la page reste utilisable (grille des packs, tarifs) au lieu d'un message bloquant.
const ABONNEMENT_PAR_DEFAUT: AbonnementInfo = {
  id: '', statut: 'TRIAL', date_fin_essai: null, date_fin: null, prix_mensuel: '0', devise: 'Ar', gratuit_jusqu_au: null, packs: [],
};

function modulesDuPack(pack: Pack): string {
  return pack === 'TOUT' ? 'Tous les modules' : MODULES_PAR_PACK[pack].map((m) => MODULE_LIBELLES[m]).join(', ');
}

export default function Abonnement() {
  const { csbId } = getSession();
  const { abonnement: abonnementServeur, acces, horsLigne, chargement, rafraichir, appliquerAbonnement } = useAbonnement();
  const abonnement = abonnementServeur ?? ABONNEMENT_PAR_DEFAUT;

  const [pack, setPack] = useState<Pack>('SANTE_BASE');
  const [duree, setDuree] = useState<DureeMois>(1);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [reference, setReference] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState<{ ok: boolean; message: string } | null>(null);

  const montant = prixPack(pack, duree);

  async function declarerPaiement() {
    if (!csbId || reference.trim().length < 4) return;
    setEnCours(true);
    setResultat(null);
    try {
      const reponse: unknown = await api.post(`/abonnements/${csbId}/paiements`, { reference: reference.trim(), pack, dureeMois: duree });
      // Le serveur renvoie { success: true, abonnement, ... } : on affiche et on met en cache local l'état à jour tout de suite.
      await appliquerAbonnement(reponse);
      setResultat({ ok: true, message: "Déclaration enregistrée. Votre abonnement sera activé après vérification par l'administrateur." });
      setReference('');
      setFormulaireOuvert(false);
      void rafraichir();
    } catch (err: any) {
      const sansReseau = !navigator.onLine || err instanceof TypeError;
      setResultat({
        ok: false,
        message: sansReseau
          ? 'Impossible de joindre le serveur (hors connexion ou serveur en veille). Vos accès ne sont pas affectés : réessayez la déclaration dans un instant.'
          : (err?.message ?? 'Erreur lors de la déclaration'),
      });
    } finally {
      setEnCours(false);
    }
  }

  if (!csbId) {
    return <p>Cette page concerne uniquement les comptes CSB.</p>;
  }

  const packsPayes = new Map(abonnement.packs.map((p) => [p.pack, p.date_fin]));
  const auMoinsUnPackActif = PACKS.some((p) => packsPayes.has(p));
  const etatInconnu = !abonnementServeur;
  const libelleStatut = acces.gratuit ? 'Mois gratuit' : etatInconnu ? 'À vérifier' : auMoinsUnPackActif ? 'Pack(s) payé(s)' : 'Aucun pack actif';

  function etatPack(p: Pack): { texte: string; ok: boolean } {
    const fin = packsPayes.get(p);
    if (fin && fin >= dateDuJour()) return { texte: `Actif jusqu'au ${fmtDate(fin)}`, ok: true };
    if (acces.gratuit) return { texte: `Inclus dans le mois gratuit (jusqu'au ${fmtDate(abonnement.gratuit_jusqu_au)})`, ok: true };
    if (etatInconnu) return { texte: 'Accès maintenu — état à vérifier', ok: true };
    return { texte: 'Non activé', ok: false };
  }

  return (
    <div>
      <h1>Mon abonnement</h1>

      <div className="registry-bar">
        <div className="registry-box">
          <span className="registry-label">Statut</span>
          <b>{libelleStatut}</b>
        </div>
        <div className="registry-box">
          <span className="registry-label">Fin du mois gratuit</span>
          <b>{fmtDate(abonnement.gratuit_jusqu_au)}</b>
        </div>
      </div>

      {etatInconnu && chargement && <p className="note">Vérification de votre abonnement…</p>}
      {etatInconnu && !chargement && (
        <p className="note">
          {horsLigne
            ? "Hors connexion : votre abonnement n'a pas encore pu être téléchargé sur cet appareil. Vos modules restent accessibles ; l'état sera vérifié dès que le serveur répondra."
            : 'Abonnement en cours de vérification.'}
        </p>
      )}
      {!etatInconnu && horsLigne && <p className="note">Hors connexion : état affiché depuis cet appareil. La déclaration d'un paiement demande une connexion.</p>}

      <div className="packs-grille">
        {PACKS.map((p) => {
          const e = etatPack(p);
          const choisi = pack === p;
          return (
            <button
              key={p}
              type="button"
              className={`pack-carte${p === 'TOUT' ? ' pack-tout' : ''}${choisi ? ' choisi' : ''}`}
              onClick={() => { setPack(p); setFormulaireOuvert(false); setResultat(null); }}
              aria-pressed={choisi}
            >
              <b>{PACK_LIBELLES[p]}</b>
              <small>{modulesDuPack(p)}</small>
              <span>{fmtAr(PRIX_MENSUEL_PACK[p])} / mois</span>
              <span>{fmtAr(prixPack(p, 3))} / 3 mois</span>
              {p === 'TOUT' && (
                <span className="pack-badge">⭐ Trimestriel : {fmtAr(prixPack('TOUT', 3))} — un seul transfert au lieu de 3</span>
              )}
              <span className={`pack-etat${e.ok ? ' ok' : ''}`}>{e.texte}</span>
            </button>
          );
        })}
      </div>

      <div className="bascule-duree" role="group" aria-label="Durée de l'abonnement">
        {DUREES_MOIS.map((d) => (
          <div key={d} className={'chip' + (duree === d ? ' selected' : '')} onClick={() => { setDuree(d); setFormulaireOuvert(false); }}>
            {d === 1 ? 'Abonnement 1 mois' : 'Abonnement 3 mois (Trimestriel)'}
          </div>
        ))}
      </div>
      <p className="note">
        💡 Économisez sur vos frais de transfert Mobile Money (Mvola, Airtel Money, Orange Money) : avec l'abonnement 3 mois,
        vous ne faites qu'<b>un seul transfert</b> au lieu de trois (Tout Activer : {fmtAr(prixPack('TOUT', 3))} pour 3 mois).
      </p>

      {!formulaireOuvert && (
        <button onClick={() => { setFormulaireOuvert(true); setResultat(null); }}>
          {auMoinsUnPackActif ? 'Renouveler' : 'Activer'} : {PACK_LIBELLES[pack]} · {duree} mois · {fmtAr(montant)}
        </button>
      )}

      {formulaireOuvert && (
        <div style={{ marginTop: 16, background: 'white', border: '1px solid #dcdcdc', borderRadius: 12, padding: 16 }}>
          <p className="note" style={{ marginTop: 0 }}>{PACK_LIBELLES[pack]} — {duree} mois</p>
          <p>
            Envoyez <strong>{montant} {abonnement.devise}</strong> via MVola au numéro{' '}
            <strong>{NUMERO_MVOLA}</strong> ({NOM_BENEFICIAIRE}), puis copiez la référence reçue par SMS et collez-la ci-dessous.
          </p>
          <label htmlFor="reference">Référence de la transaction MVola</label>
          <input
            id="reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="ex: MP260115.1234.A56789"
          />
          <div style={{ marginTop: 12 }}>
            <button onClick={declarerPaiement} disabled={reference.trim().length < 4 || enCours}>
              {enCours ? 'Envoi…' : 'Valider ma déclaration'}
            </button>
            <button className="secondaire" onClick={() => { setFormulaireOuvert(false); setReference(''); }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {resultat && <p className={resultat.ok ? '' : 'messages-erreur'} style={{ marginTop: 12 }}>{resultat.message}</p>}
    </div>
  );
}
